import axios from "axios";
import { handleAuthError, getAuthHeaders } from "../../../utils/authUtils";
import { API_BASE_URL } from "../../../environment";

export interface CustomFieldDataType {
  custom_field_data_type_id: number;
  custom_field_data_type_display_name: string;
  custom_field_data_type_key: string;
  custom_field_data_type_description: string;
}

export interface CustomFieldDependencyLevel {
  custom_field_dependency_level_id?: number;
  dependency_level: string;
  display_order: number;
  custom_field_id?: number;
  tenant_id?: number;
  created_datetime?: string;
  updated_datetime?: string;
  created_by?: number;
  updated_by?: number;
}

export interface CustomFieldChoiceOption {
  custom_field_choice_option_id?: number;
  choice_option_id?: number;
  choice_label: string;
  display_order: number;
  custom_field_id?: number;
  tenant_id?: number;
  related_entity_name?: string;
  custom_field_dependency_level_id?: number | null;
  parent_choice_option_id?: number | null;
  created_datetime?: string;
  updated_datetime?: string;
  created_by?: number;
  updated_by?: number;
}

export interface CustomField {
  custom_field_id: number;
  related_entity_name: string;
  custom_field_display_name: string;
  custom_field_label: string;
  custom_field_description?: string;
  is_active: boolean;
  is_required: boolean;
  default_value?: any;
  display_order?: number;
  created_datetime: string;
  updated_datetime: string;
  tenant_id: number;
  custom_field_data_type_id: number;
  custom_field_data_type: CustomFieldDataType;
  choice_options?: CustomFieldChoiceOption[];
  dependency_level?: CustomFieldDependencyLevel[];
  created_by?: number;
  updated_by?: number;
}

export interface CustomFieldRequest {
  related_entity_name: string;
  custom_field_data_type_id: number;
  custom_field_display_name: string;
  custom_field_label: string;
  custom_field_description?: string;
  default_value?: any;
  is_required: boolean;
  is_active: boolean;
  display_order?: number;
  tenant_id?: number;
  choice_options?: Partial<CustomFieldChoiceOption>[];
  dependency_level?: Partial<CustomFieldDependencyLevel>[];
}

export interface PaginatedCustomFieldResponse {
  results: CustomField[];
  count: number;
  next: string | null;
  previous: string | null;
  totalPages: number;
  currentPage: number;
  hasMore: boolean;
  pageSize: number;
}

const addTenantToCustomField = <T extends { tenant_id?: number | null }>(
  customField: T,
  tenantId: string | number
): T => {
  if (!tenantId) {
    throw new Error("Tenant ID not provided.");
  }
  
  return {
    ...customField,
    tenant_id: Number(tenantId),
  };
};

export const CustomFieldService = {
  getCustomFieldsByPage: async (
    tenantId: string | number,
    filters?: Record<string, any>
  ): Promise<CustomField[]> => {
    try {
      if (!tenantId) throw new Error("Tenant ID not provided.");

      const params: Record<string, any> = {
        skip_limit: true,
        tenant_id: tenantId,
        order_by: "ASC"
      };

      if (filters) {
        if (filters.searchTerm) {
          params.custom_field_display_name = filters.searchTerm;
        }
        if (filters.entityFilter) {
          params.related_entity_name = filters.entityFilter;
        }
        if (filters.typeFilter) {
          params.custom_field_data_type_id = filters.typeFilter;
        }
        if (filters.is_active !== undefined) {
          params.is_active = filters.is_active;
        }
      }

      const response = await axios.get(`${API_BASE_URL}/custom-field/`, {
        headers: getAuthHeaders(),
        params: params,
      });

      let customFields: CustomField[] = [];

      if (Array.isArray(response.data)) {
        customFields = response.data;
      } else if (response.data.results) {
        customFields = response.data.results;
      } else {
        return [];
      }

      customFields = customFields.sort((a, b) => {
        const orderA = a.display_order || 0;
        const orderB = b.display_order || 0;
        return orderA - orderB;
      });

      return customFields;
    } catch (error) {
      console.error("Error fetching custom fields page:", error);
      return handleAuthError(error);
    }
  },

  getAllCustomFields: async (tenantId: string | number): Promise<CustomField[]> => {
    try {
      const response = await CustomFieldService.getCustomFieldsByPage(tenantId);
      return response;
    } catch (error) {
      console.error("Error fetching custom fields:", error);
      return handleAuthError(error);
    }
  },

  getCustomFieldById: async (id: number): Promise<CustomField> => {
    try {
      const response = await axios.get(`${API_BASE_URL}/custom-field/${id}/`, {
        headers: getAuthHeaders(),
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching custom field:", error);
      return handleAuthError(error);
    }
  },

  createCustomField: async (customField: Partial<CustomFieldRequest>, tenantId: string | number): Promise<CustomField> => {
    try {
      const customFieldWithTenant = addTenantToCustomField(customField, tenantId);
      const response = await axios.post(
        `${API_BASE_URL}/custom-field/`,
        customFieldWithTenant,
        {
          headers: getAuthHeaders(),
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error creating custom field:", error);
      return handleAuthError(error);
    }
  },

  updateCustomField: async (id: number, customField: Partial<CustomFieldRequest>, tenantId: string | number): Promise<CustomField> => {
    try {
      const customFieldWithTenant = addTenantToCustomField(customField, tenantId);
      const response = await axios.patch(
        `${API_BASE_URL}/custom-field/${id}/`,
        customFieldWithTenant,
        {
          headers: getAuthHeaders(),
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error updating custom field:", error);
      return handleAuthError(error);
    }
  },

  deleteCustomField: async (id: number): Promise<void> => {
    try {
      await axios.delete(`${API_BASE_URL}/custom-field/${id}/`, {
        headers: getAuthHeaders(),
      });
    } catch (error) {
      console.error("Error deleting custom field:", error);
      return handleAuthError(error);
    }
  },

  getCustomFieldDataTypes: async (): Promise<CustomFieldDataType[]> => {
    try {
      const response = await axios.get(`${API_BASE_URL}/custom-field-data-type/`, {
        headers: getAuthHeaders(),
      });
      
      if (Array.isArray(response.data)) {
        return response.data;
      } else if (response.data && Array.isArray(response.data.results)) {
        return response.data.results;
      } else {
        return [];
      }
    } catch (error) {
      
      try {
        handleAuthError(error);
      } catch (authError) {
        throw authError;
      }
      
      return [];
    }
  },

  createChoiceOption: async (choiceOption: Partial<CustomFieldChoiceOption>, tenantId: string | number): Promise<CustomFieldChoiceOption> => {
    try {
      const choiceOptionWithTenant = addTenantToCustomField(choiceOption, tenantId);
      const response = await axios.post(
        `${API_BASE_URL}/custom-field-choice-option/`,
        choiceOptionWithTenant,
        {
          headers: getAuthHeaders(),
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error creating choice option:", error);
      return handleAuthError(error);
    }
  },

  updateChoiceOption: async (id: number, choiceOption: Partial<CustomFieldChoiceOption>, tenantId: string | number): Promise<CustomFieldChoiceOption> => {
    try {
      const choiceOptionWithTenant = addTenantToCustomField(choiceOption, tenantId);
      const response = await axios.patch(
        `${API_BASE_URL}/custom-field-choice-option/${id}/`,
        choiceOptionWithTenant,
        {
          headers: getAuthHeaders(),
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error updating choice option:", error);
      return handleAuthError(error);
    }
  },

  getChoiceOptionsByCustomFieldId: async (customFieldId: number): Promise<CustomFieldChoiceOption[]> => {
    try {
      const response = await axios.get(`${API_BASE_URL}/custom-field-choice-option/?custom_field_id=${customFieldId}`, {
        headers: getAuthHeaders(),
      });
      
      if (Array.isArray(response.data)) {
        return response.data;
      } else if (response.data && Array.isArray(response.data.results)) {
        return response.data.results;
      } else {
        return [];
      }
    } catch (error) {
      console.error("Error fetching choice options:", error);
      return handleAuthError(error);
    }
  },

  deleteChoiceOption: async (id: number): Promise<void> => {
    try {
      await axios.delete(`${API_BASE_URL}/custom-field-choice-option/${id}/`, {
        headers: getAuthHeaders(),
      });
    } catch (error) {
      console.error("Error deleting choice option:", error);
      return handleAuthError(error);
    }
  },

  createMultipleChoiceOptions: async (
    choiceOptions: Partial<CustomFieldChoiceOption>[],
    customFieldId: number,
    relatedEntityName: string,
    tenantId: string | number
  ): Promise<CustomFieldChoiceOption[]> => {
    try {
      const promises = choiceOptions
        .filter(option => option.choice_label && option.display_order !== undefined)
        .map(option => 
          CustomFieldService.createChoiceOption({
            choice_label: option.choice_label!,
            display_order: option.display_order!,
            custom_field_id: customFieldId,
            related_entity_name: relatedEntityName,
          }, tenantId)
        );
      return await Promise.all(promises);
    } catch (error) {
      console.error("Error creating multiple choice options:", error);
      return handleAuthError(error);
    }
  },

  createMultipleCustomFields: async (
    customFields: Partial<CustomFieldRequest>[],
    tenantId: string | number
  ): Promise<CustomField[]> => {
    try {
      const promises = customFields.map(field => 
        CustomFieldService.createCustomField(field, tenantId)
      );
      return await Promise.all(promises);
    } catch (error) {
      console.error("Error creating multiple custom fields:", error);
      return handleAuthError(error);
    }
  },

  getNextDisplayOrder: async (tenantId: string | number, entityName: string): Promise<number> => {
    try {
      const response = await CustomFieldService.getCustomFieldsByPage(tenantId, {
        entityFilter: entityName,
        is_active: true
      });
      
      const maxOrder = response.reduce((max, field) => {
        return Math.max(max, field.display_order || 0);
      }, 0);
      
      return maxOrder + 1;
    } catch (error) {
      console.error("Error getting next display order:", error);
      return 1;
    }
  },

  updateDisplayOrders: async (
    updates: { custom_field_id: number; display_order: number }[],
    tenantId: string | number
  ): Promise<void> => {
    try {
      const promises = updates.map(update => 
        CustomFieldService.updateCustomField(
          update.custom_field_id,
          { display_order: update.display_order },
          tenantId
        )
      );
      await Promise.all(promises);
    } catch (error) {
      console.error("Error updating display orders:", error);
      return handleAuthError(error);
    }
  },

  getDependencyLevelsByCustomFieldId: async (customFieldId: number): Promise<CustomFieldDependencyLevel[]> => {
    try {
      const response = await axios.get(`${API_BASE_URL}/custom-field-dependency-level/?custom_field_id=${customFieldId}`, {
        headers: getAuthHeaders(),
      });
      
      if (Array.isArray(response.data)) {
        return response.data;
      } else if (response.data && Array.isArray(response.data.results)) {
        return response.data.results;
      } else {
        return [];
      }
    } catch (error) {
      console.error("Error fetching dependency levels:", error);
      return handleAuthError(error);
    }
  },

  createDependencyLevel: async (dependencyLevel: Partial<CustomFieldDependencyLevel>, tenantId: string | number): Promise<CustomFieldDependencyLevel> => {
    try {
      const dependencyLevelWithTenant = addTenantToCustomField(dependencyLevel, tenantId);
      const response = await axios.post(
        `${API_BASE_URL}/custom-field-dependency-level/`,
        dependencyLevelWithTenant,
        {
          headers: getAuthHeaders(),
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error creating dependency level:", error);
      return handleAuthError(error);
    }
  },

  updateDependencyLevel: async (id: number, dependencyLevel: Partial<CustomFieldDependencyLevel>, tenantId: string | number): Promise<CustomFieldDependencyLevel> => {
    try {
      const dependencyLevelWithTenant = addTenantToCustomField(dependencyLevel, tenantId);
      const response = await axios.patch(
        `${API_BASE_URL}/custom-field-dependency-level/${id}/`,
        dependencyLevelWithTenant,
        {
          headers: getAuthHeaders(),
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error updating dependency level:", error);
      return handleAuthError(error);
    }
  },

  deleteDependencyLevel: async (id: number): Promise<void> => {
    try {
      await axios.delete(`${API_BASE_URL}/custom-field-dependency-level/${id}/`, {
        headers: getAuthHeaders(),
      });
    } catch (error) {
      console.error("Error deleting dependency level:", error);
      return handleAuthError(error);
    }
  },
};
